import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";
import { CARE_MNG_INCIDENTS, hasWorkspacePrivilege } from "../../utils/carePrivileges";

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const statusLabel = (value) => firstText(value, "Open").replace(/_/g, " ");
const incidentIsClosed = (record) => String(record?.status || "").toLowerCase() === "closed";
const imageAttachment = ({ url, file } = {}) => String(file?.type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)(?:\?|$)/i.test(url || "");

function IncidentAttachmentThumbnail({ url, file, label, onPreview }) {
  const [localUrl, setLocalUrl] = useState("");
  const previewUrl = url || localUrl;
  const isImage = imageAttachment({ url: previewUrl, file });

  useEffect(() => {
    if (!file) {
      setLocalUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setLocalUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <button className="incident-attachment-thumb" type="button" onClick={() => onPreview({ url: previewUrl, file, label, isImage })} disabled={!previewUrl}>
      <span>{isImage && previewUrl ? <img src={previewUrl} alt="" /> : <i className="ti ti-file-description" />}</span>
      <small>{label}</small>
      <i className="ti ti-eye" />
    </button>
  );
}
const dateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const dateTimeInputValue = (date = new Date()) => `${dateInputValue(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
const defaultIncidentDateRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  return { from: dateInputValue(from), to: dateInputValue(to) };
};
const DEFAULT_INCIDENT_DATE_RANGE = defaultIncidentDateRange();
const pendingFeatureRecordLoads = new Map();
const loadFeatureRecords = (key, request) => {
  if (pendingFeatureRecordLoads.has(key)) return pendingFeatureRecordLoads.get(key);
  const pending = request();
  pendingFeatureRecordLoads.set(key, pending);
  const clear = () => {
    if (pendingFeatureRecordLoads.get(key) === pending) pendingFeatureRecordLoads.delete(key);
  };
  pending.then(clear, clear);
  return pending;
};
const incidentSeverityTone = (value) => {
  const severity = String(value || "").trim().toLowerCase();
  if (severity === "critical") return "critical";
  if (severity === "high") return "high";
  if (severity === "medium") return "medium";
  return "low";
};

export const OCARE_MODULES = {
  compliance: {
    title: "Compliance Dashboard",
    eyebrow: "WAC 388-76 and AFH compliance",
    description: "Track compliance alerts, open documentation gaps, incident follow-up, and inspection-ready bundles.",
    icon: "ti-shield-check",
    tone: "blue",
    serviceKey: "compliance",
    createLabel: "Add compliance item",
    emptyText: "No compliance items have been added yet.",
    fields: [
      ["title", "Title", "text", "Medication record review"],
      ["category", "Category", "select", "", ["WAC 388-76", "HIPAA", "Resident rights", "Medication", "Staffing", "Documents", "Other"]],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["dueDate", "Due date", "date"],
      ["description", "Description", "textarea", "What needs to be reviewed, corrected, or bundled?"],
    ],
  },
  complianceInventory: {
    title: "Compliance Inventory",
    eyebrow: "Compliance documents and readiness",
    description: "Track compliance documents, review cycles, owners, due dates, and readiness status.",
    icon: "ti-clipboard-list",
    tone: "blue",
    serviceKey: "complianceInventory",
    createLabel: "Add inventory item",
    emptyText: "No compliance inventory items have been added yet.",
    fields: [
      ["title", "Item name", "text", "Emergency evacuation plan"],
      ["category", "Category", "select", "", ["Licensing", "Resident records", "Staff records", "Drills", "Policies", "Training", "Other"]],
      ["owner", "Owner", "text"],
      ["reviewDate", "Next review date", "date"],
      ["status", "Status", "select", "In review", ["Current", "In review", "Needs update", "Expired"]],
      ["description", "Details", "textarea", "Document location, requirements, gaps, and next actions."],
    ],
  },
  complianceAlerts: {
    title: "Compliance Alerts",
    eyebrow: "Open compliance risks and deadlines",
    description: "Monitor compliance deadlines, overdue requirements, risk flags, and follow-up actions.",
    icon: "ti-alert-triangle",
    tone: "amber",
    serviceKey: "complianceAlerts",
    createLabel: "Add alert",
    emptyText: "No compliance alerts have been added yet.",
    fields: [
      ["title", "Alert title", "text", "Staff credential expiring"],
      ["category", "Category", "select", "", ["Deadline", "Missing document", "Expired item", "Inspection", "Training", "Other"]],
      ["dueDate", "Due date", "date"],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["status", "Status", "select", "Open", ["Open", "In review", "Resolved", "Dismissed"]],
      ["description", "Details", "textarea", "Describe the risk, requirement, and follow-up needed."],
    ],
  },
  incidents: {
    title: "Incident Reporting",
    eyebrow: "Resident safety and follow-up",
    workflow: ["Report Incident", "Investigate", "Take Action"],
    description: "Document who was involved, what happened, when and where it occurred, the immediate response, notifications, supporting evidence, investigation findings, and actions taken.",
    icon: "ti-alert-triangle",
    tone: "amber",
    serviceKey: "incidents",
    createLabel: "Report incident",
    emptyText: "No incidents have been reported yet.",
    fields: [
      ["title", "Incident title", "text", "Brief, factual summary"],
      ["category", "Incident type", "select", "", [
        "Fall",
        "Medication error",
        "Injury or accident",
        "Behavioral incident",
        "Abuse, neglect, exploitation, or abandonment",
        "Elopement or missing resident",
        "Medical emergency",
        "Hospitalization or emergency-room transfer",
        "Choking or aspiration",
        "Infection or communicable disease",
        "Resident death",
        "Property damage, loss, or theft",
        "Fire, environmental, or safety hazard",
        "Staff injury",
        "Other",
      ]],
      ["location", "Where did the incident occur?", "text", ""],
      ["occurredAt", "Occurred at", "datetime-local"],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["description", "What happened", "textarea", "Describe only the facts observed, including the sequence of events."],
      ["immediateActions", "Immediate actions taken", "textarea", "First aid, assessment, supervision, emergency response, or other protective actions."],
      ["notificationsMade", "People and agencies notified", "textarea", "Family or representative, provider, RN, case manager, 911, DSHS, law enforcement, or others."],
      ["witnesses", "Witnesses", "text", "Names of witnesses, if any"],
      ["followUpRequired", "Follow-up required", "select", "Yes", ["Yes", "No"]],
      ["followUpPlan", "Follow-up plan", "textarea", "Monitoring, care-plan changes, investigation, appointments, or corrective actions."],
    ],
  },
  cbhs: {
    title: "CBHS Supportive Supervision",
    eyebrow: "Supervision and monthly reporting",
    description: "Track CBHS supportive supervision contacts, progress notes, monthly summaries, and report readiness.",
    icon: "ti-user-shield",
    tone: "purple",
    serviceKey: "cbhs",
    createLabel: "Add supervision note",
    emptyText: "No CBHS supervision records have been added yet.",
    fields: [
      ["title", "Support area", "text", "Community access supervision"],
      ["category", "Category", "select", "", ["Supportive supervision", "Behavioral support", "Community integration", "Monthly summary", "Other"]],
      ["occurredAt", "Contact date/time", "datetime-local"],
      ["progress", "Progress", "select", "Ongoing", ["Improved", "Ongoing", "Needs follow-up", "Escalated"]],
      ["description", "Details", "textarea", "Document observations, interventions, and monthly report notes."],
    ],
  },
  rnDelegation: {
    title: "RN Delegation",
    eyebrow: "Delegated nursing tasks",
    description: "Track RN delegation workflows, delegated tasks, review dates, caregiver training, and signature readiness.",
    icon: "ti-stethoscope",
    tone: "blue",
    serviceKey: "rnDelegations",
    createLabel: "Add delegation",
    emptyText: "No RN delegations have been added yet.",
    fields: [
      ["title", "Delegated task", "text", "Blood glucose check"],
      ["residentName", "Resident", "text"],
      ["delegatingRn", "Delegating RN", "text"],
      ["reviewDate", "Review date", "date"],
      ["status", "Status", "select", "Draft", ["Draft", "Active", "Needs signature", "Expired"]],
      ["description", "Instructions", "textarea", "Delegation instructions, training notes, and follow-up requirements."],
    ],
  },
  familyPortal: {
    title: "Family Portal",
    eyebrow: "Family updates and signatures",
    description: "Manage family portal access, shared updates, signature requests, and representative communication.",
    icon: "ti-users-group",
    tone: "amber",
    serviceKey: "familyPortal",
    createLabel: "Add portal item",
    emptyText: "No family portal items have been added yet.",
    fields: [
      ["title", "Title", "text", "NCP signature request"],
      ["recipient", "Family/representative", "text"],
      ["category", "Type", "select", "", ["Update", "Document share", "Signature request", "Message", "Other"]],
      ["dueDate", "Due date", "date"],
      ["description", "Message", "textarea", "What should the family member review or sign?"],
    ],
  },
  secureExchange: {
    title: "Secure Care-Team Exchange",
    eyebrow: "Fax, email, and document exchange",
    description: "Track secure fax/email exchanges with pharmacies, RNs, PCPs, case managers, and care-team partners.",
    icon: "ti-mail-lock",
    tone: "blue",
    serviceKey: "secureExchange",
    createLabel: "Log exchange",
    emptyText: "No secure exchanges have been logged yet.",
    fields: [
      ["title", "Subject", "text", "Medication clarification"],
      ["recipient", "Recipient", "text"],
      ["channel", "Channel", "select", "", ["Secure email", "Secure fax", "Document exchange", "Other"]],
      ["sentAt", "Sent/received at", "datetime-local"],
      ["description", "Details", "textarea", "Document what was sent, received, or requested."],
    ],
  },
  carePlanning: {
    title: "Meal and Activity Planning",
    eyebrow: "Daily life and ADL planning",
    description: "Plan meals, activities, ADL routines, resident preferences, and automatic task follow-up.",
    icon: "ti-tools-kitchen-2",
    tone: "amber",
    serviceKey: "carePlanning",
    createLabel: "Add plan item",
    emptyText: "No meal, activity, or ADL plan items have been added yet.",
    fields: [
      ["title", "Plan item", "text", "Hydration encouragement"],
      ["category", "Category", "select", "", ["ADL", "Meal", "Hydration", "Activity", "Preference", "Task", "Other"]],
      ["scheduledFor", "Scheduled for", "datetime-local"],
      ["frequency", "Frequency", "select", "", ["One time", "Daily", "Weekly", "Monthly", "As needed"]],
      ["description", "Details", "textarea", "Resident preferences, staff instructions, and task notes."],
    ],
  },
  hoursOperations: {
    title: "Hours and Timesheet Operations",
    eyebrow: "Hours, corrections, and disputes",
    description: "Review regular time, overtime, weekly/monthly caregiver hours, correction requests, and shift disputes.",
    icon: "ti-clock-dollar",
    tone: "purple",
    serviceKey: "hoursOperations",
    createLabel: "Add hours item",
    emptyText: "No hours operation items have been added yet.",
    fields: [
      ["title", "Title", "text", "Timesheet correction request"],
      ["caregiverName", "Caregiver", "text"],
      ["category", "Category", "select", "", ["Regular time", "Overtime", "Weekly hours", "Monthly hours", "Correction", "Dispute"]],
      ["workDate", "Work date", "date"],
      ["status", "Status", "select", "Open", ["Open", "In review", "Approved", "Denied", "Resolved"]],
      ["description", "Details", "textarea", "Correction reason, dispute notes, or report details."],
    ],
  },
};

const valueFor = (record = {}, key) =>
  firstText(record[key], record.payload?.[key], record.details?.[key]);

const peopleOptions = (payload) => {
  const items = Array.isArray(payload) ? payload : payload?.content || payload?.data || payload?.items || [];
  return items.map((item) => {
    const linkedProfile = item.userProfile || item.user || {};
    const value = firstText(item.id, item.residentId, item.careProfileId, item.profileId);
    const name = firstText(item.name, item.fullName, item.residentName, linkedProfile.name, linkedProfile.fullName, [item.firstName, item.middleName, item.lastName].filter(Boolean).join(" "), [linkedProfile.firstName, linkedProfile.middleName, linkedProfile.lastName].filter(Boolean).join(" "), item.email, linkedProfile.email, "Unnamed person");
    const email = firstText(item.email, item.primaryEmail, linkedProfile.email);
    return value ? { value, label: email ? `${name} - ${email}` : name } : null;
  }).filter(Boolean);
};

function OCareFeaturePage({ moduleKey }) {
  const module = OCARE_MODULES[moduleKey] || OCARE_MODULES.incidents;
  const listOnly = moduleKey === "complianceAlerts";
  const isIncidentModule = moduleKey === "incidents";
  const { user, privileges = [] } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const { uploadFile, uploading: incidentFilesUploading } = useFileUpload();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editingIncidentId, setEditingIncidentId] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [incidentDetailsTab, setIncidentDetailsTab] = useState("incident");
  const [investigationOpen, setInvestigationOpen] = useState(false);
  const [actionTakenOpen, setActionTakenOpen] = useState(false);
  const [incidentDetailLoading, setIncidentDetailLoading] = useState(false);
  const [investigationForm, setInvestigationForm] = useState({ summary: "", findings: "", correctiveActions: "", status: "Completed" });
  const [actionTakenForm, setActionTakenForm] = useState({ actionType: "", otherActionType: "", summary: "", preventiveActions: "", outcome: "", followUpNotes: "", staffMembers: [], status: "Completed", completedAt: dateTimeInputValue() });
  const [incidentFiles, setIncidentFiles] = useState([]);
  const [investigationFiles, setInvestigationFiles] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [incidentResidentOptions, setIncidentResidentOptions] = useState([]);
  const [incidentStaffOptions, setIncidentStaffOptions] = useState([]);
  const [incidentResidentSearchLoading, setIncidentResidentSearchLoading] = useState(false);
  const [incidentStaffSearchLoading, setIncidentStaffSearchLoading] = useState(false);
  const [incidentDateFrom, setIncidentDateFrom] = useState(DEFAULT_INCIDENT_DATE_RANGE.from);
  const [incidentDateTo, setIncidentDateTo] = useState(DEFAULT_INCIDENT_DATE_RANGE.to);
  const incidentResidentSearchTimer = useRef(null);
  const incidentStaffSearchTimer = useRef(null);
  const preloadedIncidentResidents = useRef([]);
  const preloadedIncidentStaff = useRef([]);
  const incidentFileInputRef = useRef(null);
  const investigationFileInputRef = useRef(null);

  const actorId = firstText(user?.id, user?.userId, user?.uid);
  const canManageIncidents = !isIncidentModule || hasWorkspacePrivilege(
    { privileges, user, workspace: selectedWorkspace },
    CARE_MNG_INCIDENTS,
  );
  const actorName = firstText(
    user?.name,
    user?.fullName,
    user?.displayName,
    [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" "),
    user?.email,
  );
  const workspaceName = firstText(selectedWorkspace?.organization?.name, selectedWorkspace?.organizationName, selectedWorkspace?.name, "Current workspace");
  const googleMapsApiKey = firstText(
    appConfig?.googleMapsApiKey,
    appConfig?.googleMapsApiMatrixKey,
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    process.env.VITE_GOOGLE_MAPS_API_KEY,
  );
  const api = careService[module.serviceKey];

  const visibleRecords = useMemo(
    () => [...records]
      .filter((record) => {
        if (!isIncidentModule || (!incidentDateFrom && !incidentDateTo)) return true;
        const value = valueFor(record, "occurredAt") || valueFor(record, "createdAt");
        const timestamp = new Date(value).getTime();
        if (Number.isNaN(timestamp)) return false;
        const from = incidentDateFrom ? new Date(`${incidentDateFrom}T00:00:00`).getTime() : null;
        const to = incidentDateTo ? new Date(`${incidentDateTo}T23:59:59.999`).getTime() : null;
        return (from === null || timestamp >= from) && (to === null || timestamp <= to);
      })
      .sort((a, b) => new Date(valueFor(b, "updatedAt") || valueFor(b, "createdAt") || 0) - new Date(valueFor(a, "updatedAt") || valueFor(a, "createdAt") || 0)),
    [incidentDateFrom, incidentDateTo, isIncidentModule, records],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (!api?.list || !actorId || !selectedWorkspaceId) {
      setRecords([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    const requestKey = `${module.serviceKey}:${selectedWorkspaceId}:${actorId}`;
    loadFeatureRecords(
      requestKey,
      () => api.list({ actorId, workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId }),
    )
      .then((payload) => {
        if (!active) return;
        const items = Array.isArray(payload) ? payload : payload?.content || payload?.data || payload?.items || [];
        setRecords(items);
      })
      .catch((err) => {
        if (!active) return;
        setRecords([]);
        setError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || `Unable to load ${module.title.toLowerCase()}.`));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [actorId, api, module.serviceKey, module.title, selectedWorkspaceId]);

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const openCreate = async () => {
    setEditingIncidentId(null);
    setIncidentFiles([]);
    setForm(Object.fromEntries(module.fields.map(([key, , , defaultValue]) => [
      key,
      isIncidentModule && !["severity", "followUpRequired"].includes(key)
        ? ""
        : defaultValue || "",
    ])));
    setIncidentResidentOptions([]);
    setIncidentStaffOptions([]);
    setDrawerOpen(true);
    if (!isIncidentModule || !selectedWorkspaceId) return;

    setIncidentResidentSearchLoading(true);
    setIncidentStaffSearchLoading(true);
    const params = { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId };
    const [residentsResult, staffResult] = await Promise.allSettled([
      careService.residents.listMini({ ...params, page: 0, size: 100, sort: "firstName,asc" }),
      careService.careProfiles.list(params),
    ]);
    const residentOptions = residentsResult.status === "fulfilled" ? peopleOptions(residentsResult.value) : [];
    const staffOptions = staffResult.status === "fulfilled" ? peopleOptions(staffResult.value) : [];
    preloadedIncidentResidents.current = residentOptions;
    preloadedIncidentStaff.current = staffOptions;
    setIncidentResidentOptions(residentOptions);
    setIncidentStaffOptions(staffOptions);
    setIncidentResidentSearchLoading(false);
    setIncidentStaffSearchLoading(false);
  };

  useEffect(() => () => {
    clearTimeout(incidentResidentSearchTimer.current);
    clearTimeout(incidentStaffSearchTimer.current);
  }, []);

  const searchIncidentPeople = (type, rawQuery) => {
    const query = String(rawQuery || "").trim();
    const isResident = type === "resident";
    const timerRef = isResident ? incidentResidentSearchTimer : incidentStaffSearchTimer;
    const setOptions = isResident ? setIncidentResidentOptions : setIncidentStaffOptions;
    const setSearchLoading = isResident ? setIncidentResidentSearchLoading : setIncidentStaffSearchLoading;
    clearTimeout(timerRef.current);
    if (query.length < 3 || !selectedWorkspaceId) {
      setOptions(isResident ? preloadedIncidentResidents.current : preloadedIncidentStaff.current);
      setSearchLoading(false);
      return;
    }

    timerRef.current = setTimeout(() => {
      setSearchLoading(true);
      const request = isResident
        ? careService.residents.quickSearch({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId, query, size: 12 })
        : careService.careProfiles.quickSearch({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId, query, size: 12 });
      request
        .then((payload) => {
          setOptions(peopleOptions(payload));
        })
        .catch(() => setOptions([]))
        .finally(() => setSearchLoading(false));
    }, 250);
  };

  const openIncidentDetails = async (record) => {
    setSelectedIncident(record);
    setIncidentDetailsTab("incident");
    setDetailsOpen(true);
    try {
      const params = { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId };
      const [detail, staffPayload, residentPayload] = await Promise.all([
        api.get(record.id, params),
        incidentStaffOptions.length ? Promise.resolve(null) : careService.careProfiles.list(params).catch(() => null),
        incidentResidentOptions.length ? Promise.resolve(null) : careService.residents.listMini({ ...params, page: 0, size: 100, sort: "firstName,asc" }).catch(() => null),
      ]);
      setSelectedIncident(detail);
      if (staffPayload) {
        const options = peopleOptions(staffPayload);
        preloadedIncidentStaff.current = options;
        setIncidentStaffOptions(options);
      }
      if (residentPayload) {
        const options = peopleOptions(residentPayload);
        preloadedIncidentResidents.current = options;
        setIncidentResidentOptions(options);
      }
    } catch (requestError) {
      message.error(requestError?.response?.data?.message || "Unable to load incident details.");
    }
  };

  const openIncidentEdit = async (record) => {
    if (incidentIsClosed(record)) {
      message.warning("This incident is closed and can no longer be updated.");
      return;
    }
    setIncidentFiles([]);
    setEditingIncidentId(record.id);
    setForm({ ...record, occurredAt: record.occurredAt ? String(record.occurredAt).slice(0, 16) : "" });
    setDrawerOpen(true);
    setIncidentDetailLoading(true);
    try {
      const detail = await api.get(record.id, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
      setForm({
        ...detail,
        occurredAt: detail.occurredAt ? String(detail.occurredAt).slice(0, 16) : "",
        residentsInvolved: (detail.residentIds || []).map((value) => ({ value, label: "Selected resident" })),
        staffInvolved: (detail.staffIds || []).map((value) => ({ value, label: "Selected staff member" })),
      });
    } catch (requestError) {
      setDrawerOpen(false);
      message.error(requestError?.response?.data?.message || "Unable to load the incident for editing.");
    } finally {
      setIncidentDetailLoading(false);
    }
  };

  const openInvestigation = async (record) => {
    if (incidentIsClosed(record)) {
      message.warning("This incident is closed and can no longer be updated.");
      return;
    }
    setInvestigationFiles([]);
    setSelectedIncident(record);
    setInvestigationOpen(true);
    setIncidentDetailLoading(true);
    try {
      const detail = await api.get(record.id, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
      setSelectedIncident(detail);
      setInvestigationForm({
        summary: detail.investigationSummary || "",
        findings: detail.investigationFindings || "",
        correctiveActions: detail.correctiveActions || "",
        status: detail.investigationStatus || "Completed",
      });
    } catch (requestError) {
      setInvestigationOpen(false);
      message.error(requestError?.response?.data?.message || "Unable to load the incident investigation.");
    } finally {
      setIncidentDetailLoading(false);
    }
  };

  const openActionTaken = async (record) => {
    if (incidentIsClosed(record)) {
      message.warning("This incident is closed and can no longer be updated.");
      return;
    }
    if (String(record.investigationStatus || "").toLowerCase() !== "completed") {
      message.warning("Complete the investigation report before recording action taken.");
      return;
    }
    setSelectedIncident(record);
    setIncidentDetailLoading(true);
    setActionTakenOpen(true);
    try {
      const detail = await api.get(record.id, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
      setSelectedIncident(detail);
      setActionTakenForm({
        actionType: detail.actionTakenType || "",
        otherActionType: detail.actionTakenOtherType || "",
        summary: detail.actionTakenSummary || "",
        preventiveActions: detail.actionTakenPreventiveActions || "",
        outcome: detail.actionTakenOutcome || "",
        followUpNotes: detail.actionTakenFollowUpNotes || "",
        staffMembers: (detail.actionTakenStaffIds || []).map((value) => ({ value, label: incidentStaffOptions.find((option) => option.value === value)?.label || "Selected staff member" })),
        status: detail.actionTakenStatus || "Completed",
        completedAt: detail.actionTakenAt ? String(detail.actionTakenAt).slice(0, 16) : dateTimeInputValue(),
      });
      if (!incidentStaffOptions.length) {
        const options = peopleOptions(await careService.careProfiles.list({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }));
        preloadedIncidentStaff.current = options;
        setIncidentStaffOptions(options);
        setActionTakenForm((current) => ({
          ...current,
          staffMembers: (detail.actionTakenStaffIds || []).map((value) => ({ value, label: options.find((option) => option.value === value)?.label || "Selected staff member" })),
        }));
      }
    } catch (requestError) {
      setActionTakenOpen(false);
      message.error(requestError?.response?.data?.message || "Unable to load action taken details.");
    } finally {
      setIncidentDetailLoading(false);
    }
  };

  const saveActionTaken = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await api.recordActionTaken(selectedIncident.id, {
        ...actionTakenForm,
        staffIds: (actionTakenForm.staffMembers || []).map((item) => item?.value).filter(Boolean),
      }, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
      setRecords((current) => current.map((item) => item.id === saved.id ? saved : item));
      setActionTakenOpen(false);
      message.success("Action taken details saved.");
    } catch (requestError) {
      message.error(requestError?.response?.data?.message || "Unable to save action taken details.");
    } finally {
      setSaving(false);
    }
  };

  const closeIncident = (record) => {
    let closingNotes = "";
    Modal.confirm({
      title: "Close this incident?",
      content: <div className="incident-close-confirm"><p>Closing locks the incident, investigation report, and actions taken. No further updates will be allowed.</p><label><span>Closing notes <em>Optional</em></span><textarea rows={4} placeholder="Add final context, handoff details, or the reason this incident is ready to close." onChange={(event) => { closingNotes = event.target.value; }} /></label></div>,
      okText: "Close incident",
      okButtonProps: { danger: true },
      onOk: async () => {
        const closed = await api.close(record.id, { notes: closingNotes }, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
        setSelectedIncident(closed);
        setRecords((current) => current.map((item) => item.id === closed.id ? { ...item, status: closed.status, updatedAt: closed.updatedAt } : item));
        message.success("Incident closed and locked from further updates.");
      },
    });
  };

  const printIncident = () => {
    const record = document.querySelector(".incident-print-record");
    const printWindow = window.open("", "_blank", "width=960,height=760");
    if (!record || !printWindow) {
      message.error("Unable to open the incident print preview. Allow pop-ups and try again.");
      return;
    }
    const printStyles = `
      @page { size: auto; margin: 14mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #1e2d24; }
      .incident-print-record { display: block !important; width: 100%; font: 10pt/1.45 Arial, sans-serif; }
      .incident-print-record > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; padding-bottom: 14px; border-bottom: 3px solid #15803d; }
      .incident-print-record > header span { color: #1769aa; font-size: 9pt; font-weight: 800; text-transform: uppercase; }
      .incident-print-record > header h1 { margin: 3px 0; color: #153c22; font-size: 21pt; line-height: 1.2; }
      .incident-print-record > header p { margin: 0; color: #52645a; }
      .incident-print-record > header > strong { padding: 5px 10px; border: 1px solid #9fd6ad; border-radius: 4px; color: #137333; font-size: 9pt; text-transform: uppercase; }
      .incident-print-summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin: 14px 0; }
      .incident-print-summary > div { padding: 8px 10px; border: 1px solid #dce8df; break-inside: avoid; }
      .incident-print-summary span, .incident-print-record dt { display: block; margin-bottom: 2px; color: #607067; font-size: 8pt; font-weight: 800; text-transform: uppercase; }
      .incident-print-record > section:not(.incident-print-summary) { margin-top: 16px; }
      .incident-print-record h2 { margin: 0 0 8px; padding-bottom: 5px; border-bottom: 1px solid #9fc8e9; color: #1769aa; font-size: 14pt; }
      .incident-print-record dl { display: grid; gap: 7px; margin: 0; }
      .incident-print-record dl > div { padding: 7px 9px; border-left: 3px solid #b7dfc1; background: #f8fbf9; break-inside: avoid; }
      .incident-print-record dd { margin: 0; color: #263c2d; white-space: pre-wrap; }
      .incident-print-meta { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 8px; color: #52645a; font-size: 9pt; }
      .incident-print-record footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #dce8df; color: #6d7b72; font-size: 8pt; text-align: right; }
    `;
    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Incident - ${String(selectedIncident?.title || "Record").replace(/[<>]/g, "")}</title><style>${printStyles}</style></head><body>${record.outerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 150);
  };

  const deleteIncident = (record) => {
    Modal.confirm({
      title: "Delete incident report?",
      content: "This permanently removes the incident and its investigation report.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await api.delete(record.id, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
        setRecords((current) => current.filter((item) => item.id !== record.id));
        message.success("Incident deleted.");
      },
    });
  };

  const saveInvestigation = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const uploadedFileIds = await uploadIncidentFiles(investigationFiles);
      const saved = await api.addInvestigation(selectedIncident.id, {
        ...investigationForm,
        attachmentFileIds: uploadedFileIds,
      }, { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId });
      setRecords((current) => current.map((item) => item.id === saved.id ? saved : item));
      setInvestigationOpen(false);
      message.success("Investigation report saved.");
    } catch (requestError) {
      message.error(requestError?.response?.data?.message || "Unable to save investigation report.");
    } finally {
      setSaving(false);
    }
  };

  const addPendingFiles = (setter, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) setter((current) => [...current, ...files]);
    event.target.value = "";
  };

  const uploadIncidentFiles = async (files) => {
    const fileIds = [];
    for (const file of files) {
      const uploaded = await uploadFile(null, file, {
        confirmUpload: false,
        fileType: file.type || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      const fileId = uploaded?.fileId || uploaded?.file?.id;
      if (!fileId) throw new Error(`Storage upload did not return a file ID for ${file.name}.`);
      fileIds.push(fileId);
    }
    return fileIds;
  };

  const saveRecord = async (event) => {
    event.preventDefault();
    if (!api?.create) {
      message.error(`${module.title} is not connected to the care service yet.`);
      return;
    }
    setSaving(true);
    setError("");
    const now = new Date().toISOString();
    const incidentForm = (() => {
      if (!isIncidentModule) return form;
      const { residentsInvolved = [], staffInvolved = [], ...incidentValues } = form;
      return {
        ...incidentValues,
        residentIds: residentsInvolved.map((item) => item?.value).filter(Boolean),
        staffIds: staffInvolved.map((item) => item?.value).filter(Boolean),
      };
    })();
    const payload = {
      ...incidentForm,
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      createdById: actorId,
      reportedById: actorId,
      ...(!isIncidentModule ? { createdByName: actorName, reportedByName: actorName } : {}),
      status: form.status || "Open",
      createdAt: now,
      updatedAt: now,
    };
    try {
      if (isIncidentModule) {
        payload.attachmentFileIds = [...(form.attachmentFileIds || []), ...await uploadIncidentFiles(incidentFiles)];
      }
      const saved = editingIncidentId
        ? await api.update(editingIncidentId, payload, { actorId, workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
        : await api.create(payload, { actorId, workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId });
      setRecords((current) => editingIncidentId
        ? current.map((item) => item.id === editingIncidentId ? saved : item)
        : [saved || payload, ...current]);
      setDrawerOpen(false);
      message.success(editingIncidentId ? "Incident updated." : `${module.title} item saved.`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Unable to save ${module.title.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const renderFormField = ([key, label, type, placeholder, options]) => key === "location" && isIncidentModule ? (
    <label className="ncp-form-field incident-location-field" key={key}>
      <span>{label}</span>
      {appConfigLoading && !googleMapsApiKey ? (
        <input type="text" disabled placeholder="Loading location search..." />
      ) : (
        <GooglePlacesAutocompleteInput
          googleMapsApiKey={googleMapsApiKey}
          placeholder={googleMapsApiKey ? "Search for the incident location" : "Enter the incident location"}
          types={["address"]}
          value={form.location || ""}
          onChange={(value) => {
            updateField("location", value);
            updateField("locationDetails", null);
          }}
          onResolvePlace={({ details, prediction }) => {
            const address = firstText(details?.formatted_address, prediction?.description);
            const latitude = details?.geometry?.location?.lat?.();
            const longitude = details?.geometry?.location?.lng?.();
            setForm((current) => ({
              ...current,
              location: address,
              locationDetails: {
                formattedAddress: address,
                placeId: firstText(details?.place_id, prediction?.placeId),
                latitude: Number.isFinite(latitude) ? latitude : null,
                longitude: Number.isFinite(longitude) ? longitude : null,
              },
            }));
          }}
        />
      )}
    </label>
  ) : (
    <label className={`ncp-form-field${type === "textarea" ? " wide" : ""}`} key={key}>
      <span>{label}</span>
      {type === "select" ? (
        <select value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)} required={isIncidentModule && key === "category"}>
          <option value="">Select</option>
          {(options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)} placeholder={placeholder || ""} rows={4} />
      ) : (
        <input type={type || "text"} value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)} placeholder={placeholder || ""} required={isIncidentModule && ["title", "occurredAt"].includes(key)} />
      )}
    </label>
  );

  const incidentFields = Object.fromEntries(module.fields.map((field) => [field[0], field]));
  const renderIncidentFields = (keys) => keys.map((key) => incidentFields[key]).filter(Boolean).map(renderFormField);

  return (
    <CareShell className={`dashboard-content app-page-content emergency-evacuation-drills-page${isIncidentModule ? " incident-reporting-page" : ""}`}>
      <section className={`care-page-herodd residents-page-head${isIncidentModule ? " incident-reporting-head" : ""}`}>
        <div className={isIncidentModule ? "incident-page-heading" : ""}>
          {isIncidentModule ? <span className="incident-page-heading-icon"><i className="ti ti-shield-exclamation" /></span> : null}
          <div className="page-title">
            <span>{module.eyebrow}</span>
            <h1>{module.title}</h1>
            {module.workflow ? <div className="incident-workflow-instruction">{module.workflow.map((step, index) => <span key={step}>{index ? <i className="ti ti-arrow-right" /> : null}<strong>{step}</strong></span>)}</div> : null}
            <p>{module.description}</p>
          </div>
        </div>
        {!listOnly && (!isIncidentModule || canManageIncidents) ? (
          <button className={`mini-btn emergency-evacuation-schedule-button${isIncidentModule ? " incident-report-button" : ""}`} type="button" onClick={openCreate}>
            <i className={`ti ${isIncidentModule ? "ti-alert-triangle" : "ti-plus"}`} /> {module.createLabel}
          </button>
        ) : null}
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      {!listOnly && !isIncidentModule ? <div className="stats-grid emergency-evacuation-drill-summary-grid">
        <Panel title="Workspace">
          <div className="list-row">
            <span className={`round-icon ${module.tone}`}><i className={`ti ${module.icon}`} /></span>
            <div><strong>{workspaceName}</strong><small>{module.eyebrow}</small></div>
            <em>Active</em>
          </div>
        </Panel>
        <Panel title="Records">
          <div className="list-row">
            <span className="round-icon blue"><i className="ti ti-list-check" /></span>
            <div><strong>{visibleRecords.length}</strong><small>Total records in this module</small></div>
            <em>{loading ? "Loading" : "Ready"}</em>
          </div>
        </Panel>
      </div> : null}

      <Panel title={isIncidentModule ? "Previous incidents" : module.title} className={isIncidentModule ? "incident-history-panel" : ""}>
        {isIncidentModule ? (
          <div className="incident-history-toolbar">
            <div className="incident-history-toolbar-copy">
              <strong>{visibleRecords.length} shown</strong>
              <span>{visibleRecords.length === records.length ? "All reported incidents" : `Filtered from ${records.length} incidents`}</span>
            </div>
            <div className="incident-history-date-fields">
              <label><span>From</span><input type="date" value={incidentDateFrom} max={incidentDateTo || undefined} onChange={(event) => setIncidentDateFrom(event.target.value)} /></label>
              <label><span>To</span><input type="date" value={incidentDateTo} min={incidentDateFrom || undefined} onChange={(event) => setIncidentDateTo(event.target.value)} /></label>
              {incidentDateFrom || incidentDateTo ? (
                <button type="button" className="mini-btn" onClick={() => { setIncidentDateFrom(""); setIncidentDateTo(""); }} title="Clear date range" aria-label="Clear date range">
                  <i className="ti ti-x" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="resident-tab-list" style={{ "--resident-record-columns": `minmax(220px, 1fr) 145px 130px minmax(220px, 1.2fr) 140px ${isIncidentModule ? "210px" : "28px"}` }}>
          <div className="resident-tab-list-row resident-tab-list-head-row">
            <span>Title</span>
            <span>Category</span>
            <span>{isIncidentModule ? "Severity / status" : "Status"}</span>
            <span>Details</span>
            <span>{isIncidentModule ? "Occurred" : "Updated"}</span>
            <span>{isIncidentModule ? "Actions" : ""}</span>
          </div>
          {visibleRecords.map((record, index) => (
            <article className="resident-tab-list-row" key={record.id || `${moduleKey}-${index}`}>
              <span><strong>{valueFor(record, "title") || valueFor(record, "name") || module.title}</strong></span>
              <span>{valueFor(record, "category") || valueFor(record, "channel") || valueFor(record, "type") || "General"}</span>
              <span>{isIncidentModule ? (
                <span className="incident-row-badges">
                  <em className={`incident-severity-pill ${incidentSeverityTone(valueFor(record, "severity"))}`}>{firstText(valueFor(record, "severity"), "Low")}</em>
                  <em className="pill blue">{statusLabel(valueFor(record, "status"))}</em>
                </span>
              ) : <em className="pill blue">{statusLabel(valueFor(record, "status"))}</em>}</span>
              <span>{valueFor(record, "descriptionPreview") || valueFor(record, "description") || valueFor(record, "summary") || "Open details to review this incident."}</span>
              <span>{formatDateTime(isIncidentModule ? valueFor(record, "occurredAt") || valueFor(record, "createdAt") : valueFor(record, "updatedAt") || valueFor(record, "createdAt"))}</span>
              {isIncidentModule ? (
                <span className="incident-row-actions">
                  <button type="button" className="view" onClick={() => openIncidentDetails(record)} title="View details" aria-label="View incident details"><i className="ti ti-eye" /></button>
                  {canManageIncidents ? <><button type="button" className="edit" onClick={() => openIncidentEdit(record)} disabled={incidentIsClosed(record)} title={incidentIsClosed(record) ? "Closed incidents cannot be updated" : "Update incident"} aria-label="Update incident"><i className="ti ti-edit" /></button><button type="button" className="investigate" onClick={() => openInvestigation(record)} disabled={incidentIsClosed(record)} title={incidentIsClosed(record) ? "Closed incidents cannot be investigated" : "Add investigation report"} aria-label="Add investigation report"><i className="ti ti-file-search" /></button><button type="button" className="action-taken" onClick={() => openActionTaken(record)} disabled={incidentIsClosed(record) || String(record.investigationStatus || "").toLowerCase() !== "completed"} title={incidentIsClosed(record) ? "Closed incidents cannot be updated" : String(record.investigationStatus || "").toLowerCase() === "completed" ? "Record action taken" : "Complete the investigation first"} aria-label="Record action taken"><i className="ti ti-list-check" /></button>{!record.actionTakenStatus && !incidentIsClosed(record) ? <button type="button" className="danger" onClick={() => deleteIncident(record)} title="Delete incident" aria-label="Delete incident"><i className="ti ti-trash" /></button> : null}</> : null}
                </span>
              ) : <i className="ti ti-chevron-right" />}
            </article>
          ))}
          {loading ? <div className="resident-inline-empty">Loading records...</div> : null}
          {!loading && !visibleRecords.length ? isIncidentModule ? (
            <div className="incident-history-empty">
              <span><i className="ti ti-shield-check" /></span>
              <strong>{incidentDateFrom || incidentDateTo ? "No incidents in this date range" : "No incidents reported"}</strong>
              <p>{incidentDateFrom || incidentDateTo ? "Adjust or clear the date filters to review other reports." : "Reported incidents will appear here for review and follow-up."}</p>
            </div>
          ) : <div className="resident-inline-empty">{module.emptyText}</div> : null}
        </div>
      </Panel>

      {!listOnly && !isIncidentModule ? <Panel title="Related OCarely Workflows">
        <div className="action-grid">
          <Link className="action-card" to="/residents">
            <span className="round-icon blue"><i className="ti ti-users" /></span>
            <div><h2>Residents</h2><p>Open resident records, documents, assessments, and daily notes.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
          <Link className="action-card" to="/resident-medications">
            <span className="round-icon amber"><i className="ti ti-pill" /></span>
            <div><h2>eMAR</h2><p>Review medication orders, administration windows, and pharmacy workflows.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
          <Link className="action-card" to="/timesheets">
            <span className="round-icon purple"><i className="ti ti-clock-dollar" /></span>
            <div><h2>Hours</h2><p>Review caregiver hours, timesheets, corrections, and overtime.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
        </div>
      </Panel> : null}

      {!listOnly ? <Drawer
        title={isIncidentModule ? (
          <div className="incident-drawer-title">
            <span className="incident-drawer-title-icon"><i className="ti ti-shield-plus" /></span>
            <div className="incident-drawer-title-copy">
              <strong>{editingIncidentId ? "Update Incident" : "Report Incident"}</strong>
              <small>Document facts, response, and required follow-up</small>
            </div>
          </div>
        ) : module.createLabel}
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={isIncidentModule ? "min(1100px, 97vw)" : 560}
        className={`resident-action-drawer${isIncidentModule ? " incident-report-drawer" : ""}`}
      >
        <form className="assessment-drawer-form" onSubmit={saveRecord}>
          <section className="assessment-drawer-section">
            {isIncidentModule ? (
              <div className="incident-form-sections">
                <section className="incident-form-section">
                  <header className="incident-form-section-head">
                    <span><i className="ti ti-users" /></span>
                    <div><h3>People involved</h3><p>Select every resident and staff member directly involved.</p></div>
                  </header>
                  <div className="assessment-drawer-grid incident-people-grid">
                  <label className="ncp-form-field">
                    <span>Residents involved</span>
                    <Select
                      mode="multiple"
                      labelInValue
                      showSearch
                      filterOption={false}
                      value={form.residentsInvolved || []}
                      options={incidentResidentOptions}
                      loading={incidentResidentSearchLoading}
                      onSearch={(query) => searchIncidentPeople("resident", query)}
                      onChange={(value) => updateField("residentsInvolved", value)}
                      placeholder="Search and select residents"
                      notFoundContent={incidentResidentSearchLoading ? "Loading residents..." : "No residents found"}
                    />
                  </label>
                  <label className="ncp-form-field">
                    <span>Staff involved</span>
                    <Select
                      mode="multiple"
                      labelInValue
                      showSearch
                      filterOption={false}
                      value={form.staffInvolved || []}
                      options={incidentStaffOptions}
                      loading={incidentStaffSearchLoading}
                      onSearch={(query) => searchIncidentPeople("staff", query)}
                      onChange={(value) => updateField("staffInvolved", value)}
                      placeholder="Search and select staff"
                      notFoundContent={incidentStaffSearchLoading ? "Loading staff..." : "No staff found"}
                    />
                  </label>
                  </div>
                </section>
                <section className="incident-form-section">
                  <header className="incident-form-section-head">
                    <span><i className="ti ti-file-description" /></span>
                    <div><h3>Incident details</h3><p>Record when, where, and what type of incident occurred.</p></div>
                  </header>
                  <div className="assessment-drawer-grid">
                    {renderIncidentFields(["title", "category"])}
                  </div>
                  <div className="assessment-drawer-grid">
                    {renderIncidentFields(["location", "occurredAt", "severity"])}
                  </div>
                </section>
                <section className="incident-form-section incident-response-section">
                  <header className="incident-form-section-head">
                    <span><i className="ti ti-first-aid-kit" /></span>
                    <div><h3>Response and notifications</h3><p>Document the observed facts, safeguards, and notifications made.</p></div>
                  </header>
                  <div className="assessment-drawer-grid">
                    {renderIncidentFields(["description", "immediateActions", "notificationsMade"])}
                  </div>
                </section>
                <section className="incident-form-section">
                  <header className="incident-form-section-head">
                    <span><i className="ti ti-list-check" /></span>
                    <div><h3>Witnesses and follow-up</h3><p>Capture witnesses and the next actions needed to close the incident.</p></div>
                  </header>
                  <div className="assessment-drawer-grid">
                    {renderIncidentFields(["witnesses", "followUpRequired", "followUpPlan"])}
                  </div>
                </section>
                <section className="incident-form-section incident-evidence-section">
                  <header className="incident-form-section-head">
                    <span><i className="ti ti-paperclip" /></span>
                    <div><h3>Incident evidence</h3><p>Capture a photo or attach supporting files.</p></div>
                  </header>
                  <div className="incident-privacy-badge"><i className="ti ti-shield-lock" /><span><strong>Protect resident privacy.</strong> Do not add photos that compromise a resident's privacy or dignity.</span></div>
                  <div className="incident-file-actions">
                    <input ref={incidentFileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" hidden onChange={(event) => addPendingFiles(setIncidentFiles, event)} />
                    <button type="button" className="mini-btn" onClick={() => incidentFileInputRef.current?.click()}><i className="ti ti-paperclip" /> Attach Supporting Photos &amp; Docs</button>
                  </div>
                  {incidentFiles.length || form.attachmentFileIds?.length ? <div className="incident-attachment-thumbnails">
                    {(form.attachmentFileIds || []).map((fileId, index) => {
                      const attachment = (form.attachments || []).find((item) => item.fileId === fileId);
                      return <IncidentAttachmentThumbnail key={fileId} url={attachment?.signedUrl} label={`Attachment ${index + 1}`} onPreview={setAttachmentPreview} />;
                    })}
                    {incidentFiles.map((file, index) => <IncidentAttachmentThumbnail key={`${file.name}-${file.lastModified}-${index}`} file={file} label={file.name} onPreview={setAttachmentPreview} />)}
                  </div> : null}
                  {incidentFiles.length || form.attachmentFileIds?.length ? <div className="incident-file-list">
                    {(form.attachmentFileIds || []).map((fileId, index) => {
                      const attachment = (form.attachments || []).find((item) => item.fileId === fileId);
                      return <span key={fileId}><i className="ti ti-file-check" />{attachment?.signedUrl ? <a href={attachment.signedUrl} target="_blank" rel="noreferrer">Saved attachment {index + 1}</a> : <span>Saved attachment {index + 1}</span>}<button type="button" onClick={() => setForm((current) => ({ ...current, attachmentFileIds: (current.attachmentFileIds || []).filter((id) => id !== fileId), attachments: (current.attachments || []).filter((item) => item.fileId !== fileId) }))} aria-label={`Remove saved attachment ${index + 1}`}><i className="ti ti-x" /></button></span>;
                    })}
                    {incidentFiles.map((file, index) => <span key={`${file.name}-${file.lastModified}-${index}`}><i className="ti ti-file" /> {file.name}<button type="button" onClick={() => setIncidentFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove ${file.name}`}><i className="ti ti-x" /></button></span>)}
                  </div> : null}
                </section>
              </div>
            ) : (
              <>
                <h3>{module.title}</h3>
                <div className="assessment-drawer-grid">{module.fields.map(renderFormField)}</div>
              </>
            )}
          </section>
          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={() => setDrawerOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn primary" type="submit" disabled={saving || incidentDetailLoading || incidentFilesUploading}>
              {incidentDetailLoading ? <><i className="ti ti-loader-2" /> Loading...</> : saving || incidentFilesUploading ? <><i className="ti ti-loader-2" /> Saving...</> : "Save"}
            </button>
          </div>
        </form>
      </Drawer> : null}

      {isIncidentModule ? <Drawer title={<div className="incident-details-title"><span><i className="ti ti-shield-check" /></span><div><strong>Incident Record</strong><small>Review the report, response, and investigation</small></div></div>} placement="right" width="min(840px, 96vw)" open={detailsOpen} onClose={() => setDetailsOpen(false)} className="resident-action-drawer incident-details-drawer">
        {selectedIncident ? <div className="incident-detail-sheet">
          <header className="incident-detail-hero"><div className="incident-detail-hero-copy"><span className="incident-detail-category">{selectedIncident.category}</span><h2>{selectedIncident.title}</h2><div className="incident-detail-badges"><span className={`incident-severity-pill ${incidentSeverityTone(selectedIncident.severity)}`}>{selectedIncident.severity}</span><em>{statusLabel(selectedIncident.status)}</em></div></div><span className="incident-detail-hero-icon"><i className="ti ti-alert-triangle" /></span></header>
          <div className="incident-detail-actions"><button type="button" className="btn incident-detail-print" onClick={printIncident}><i className="ti ti-printer" /> Print</button>{incidentIsClosed(selectedIncident) ? <span className="incident-closed-notice"><i className="ti ti-lock" /> Incident closed</span> : canManageIncidents ? <><button type="button" className="btn incident-detail-update" onClick={() => { setDetailsOpen(false); openIncidentEdit(selectedIncident); }}><i className="ti ti-edit" /> Update incident</button><button type="button" className="btn incident-detail-investigation" onClick={() => { setDetailsOpen(false); openInvestigation(selectedIncident); }}><i className="ti ti-file-search" /> Investigation report</button>{String(selectedIncident.investigationStatus || "").toLowerCase() === "completed" ? <button type="button" className="btn incident-detail-action-taken" onClick={() => { setDetailsOpen(false); openActionTaken(selectedIncident); }}><i className="ti ti-list-check" /> Action taken</button> : null}<button type="button" className="btn incident-detail-close" onClick={() => closeIncident(selectedIncident)}><i className="ti ti-lock" /> Close incident</button></> : null}</div>
          <nav className="incident-detail-tabs" aria-label="Incident record sections">
            <button type="button" className={incidentDetailsTab === "incident" ? "active" : ""} onClick={() => setIncidentDetailsTab("incident")}><i className="ti ti-file-description" /> Incident Details</button>
            <button type="button" className={incidentDetailsTab === "investigation" ? "active" : ""} onClick={() => setIncidentDetailsTab("investigation")}><i className="ti ti-file-search" /> Investigation Report</button>
            <button type="button" className={incidentDetailsTab === "actions" ? "active" : ""} onClick={() => setIncidentDetailsTab("actions")}><i className="ti ti-list-check" /> Actions Taken</button>
          </nav>
          {incidentDetailsTab === "incident" ? <div className="incident-detail-tab-panel">
            <div className="incident-detail-facts"><div><span><i className="ti ti-calendar-event" /> Occurred</span><strong>{formatDateTime(selectedIncident.occurredAt)}</strong></div><div><span><i className="ti ti-map-pin" /> Location</span><strong>{selectedIncident.location || "Not recorded"}</strong></div><div><span><i className="ti ti-list-check" /> Follow-up</span><strong>{selectedIncident.followUpRequired || "Not recorded"}</strong></div></div>
            <section className="incident-detail-group"><header><span><i className="ti ti-file-description" /></span><div><h3>Incident narrative</h3><p>Reported facts and observations</p></div></header><div className="incident-detail-copy"><span>What happened</span><p>{selectedIncident.description || "No narrative was recorded."}</p></div></section>
            <section className="incident-detail-group"><header><span><i className="ti ti-first-aid-kit" /></span><div><h3>Response and notifications</h3><p>Immediate safeguards and people notified</p></div></header><div className="incident-detail-grid"><div><span>Immediate actions</span><p>{selectedIncident.immediateActions || "Not recorded"}</p></div><div><span>People and agencies notified</span><p>{selectedIncident.notificationsMade || "Not recorded"}</p></div></div></section>
            <section className="incident-detail-group"><header><span><i className="ti ti-route" /></span><div><h3>Witnesses and follow-up</h3><p>Supporting observations and next actions</p></div></header><div className="incident-detail-grid"><div><span>Witnesses</span><p>{selectedIncident.witnesses || "None recorded"}</p></div><div><span>Follow-up plan</span><p>{selectedIncident.followUpPlan || "Not recorded"}</p></div></div></section>
            {incidentIsClosed(selectedIncident) ? <section className="incident-detail-group closure"><header><span><i className="ti ti-lock" /></span><div><h3>Incident closure</h3><p>{selectedIncident.closedAt ? `Closed ${formatDateTime(selectedIncident.closedAt)}` : "Closed"}</p></div></header><div className="incident-detail-copy"><span>Closing notes</span><p>{selectedIncident.closingNotes || "No closing notes were recorded."}</p></div></section> : null}
            {selectedIncident.attachments?.length ? <section className="incident-detail-group evidence"><header><span><i className="ti ti-paperclip" /></span><div><h3>Supporting evidence</h3><p>{selectedIncident.attachments.length} attachment{selectedIncident.attachments.length === 1 ? "" : "s"}</p></div></header><div className="incident-attachment-thumbnails">{selectedIncident.attachments.map((attachment, index) => <IncidentAttachmentThumbnail key={attachment.fileId} url={attachment.signedUrl} label={`Attachment ${index + 1}`} onPreview={setAttachmentPreview} />)}</div></section> : null}
          </div> : null}
          {incidentDetailsTab === "investigation" ? <div className="incident-detail-tab-panel">{selectedIncident.investigationSummary || selectedIncident.investigationFindings || selectedIncident.correctiveActions ? <section className="incident-detail-group investigation"><header><span><i className="ti ti-file-search" /></span><div><h3>Investigation report</h3><p>{selectedIncident.investigationStatus || "In progress"}{selectedIncident.investigatedAt ? ` · ${formatDateTime(selectedIncident.investigatedAt)}` : ""}</p></div></header><div className="incident-detail-stack">{[["Summary", selectedIncident.investigationSummary], ["Findings", selectedIncident.investigationFindings], ["Corrective actions", selectedIncident.correctiveActions]].filter(([, value]) => value).map(([label, value]) => <div key={label}><span>{label}</span><p>{value}</p></div>)}</div></section> : <div className="incident-detail-empty"><i className="ti ti-file-search" /><strong>No investigation report yet</strong><p>Complete the investigation before recording final actions.</p></div>}</div> : null}
          {incidentDetailsTab === "actions" ? <div className="incident-detail-tab-panel">{selectedIncident.actionTakenSummary ? <section className="incident-detail-group action-taken"><header><span><i className="ti ti-list-check" /></span><div><h3>Actions taken</h3><p>{selectedIncident.actionTakenStatus || "Completed"}{selectedIncident.actionTakenAt ? ` · ${formatDateTime(selectedIncident.actionTakenAt)}` : ""}</p></div></header><div className="incident-detail-stack">{[["Action type", selectedIncident.actionTakenType === "Other" ? selectedIncident.actionTakenOtherType || "Other" : selectedIncident.actionTakenType], ["What actions were taken", selectedIncident.actionTakenSummary], ["Prevention of future occurrences", selectedIncident.actionTakenPreventiveActions], ["Outcome", selectedIncident.actionTakenOutcome], ["Follow-up notes", selectedIncident.actionTakenFollowUpNotes], ["Action taken by", (selectedIncident.actionTakenStaffIds || []).map((id) => incidentStaffOptions.find((option) => option.value === id)?.label || id).join(", ")]].filter(([, value]) => value).map(([label, value]) => <div key={label}><span>{label}</span><p>{value}</p></div>)}</div></section> : <div className="incident-detail-empty"><i className="ti ti-list-check" /><strong>No actions recorded yet</strong><p>{String(selectedIncident.investigationStatus || "").toLowerCase() === "completed" ? "Record the action completed in response to the investigation." : "Actions can be recorded after the investigation is completed."}</p></div>}</div> : null}
        </div> : null}
      </Drawer> : null}

      {isIncidentModule ? <Drawer title="Investigation report" placement="right" width="min(720px, 96vw)" open={investigationOpen} onClose={() => setInvestigationOpen(false)} className="resident-action-drawer incident-investigation-drawer">
        <form className="assessment-drawer-form" onSubmit={saveInvestigation}>
          <section className="assessment-drawer-section"><h3>{selectedIncident?.title}</h3><div className="assessment-drawer-grid">
            <label className="ncp-form-field wide"><span>Investigation summary</span><textarea rows={4} required value={investigationForm.summary} onChange={(event) => setInvestigationForm((current) => ({ ...current, summary: event.target.value }))} placeholder="Summarize the investigation process and scope." /></label>
            <label className="ncp-form-field wide"><span>Findings</span><textarea rows={4} value={investigationForm.findings} onChange={(event) => setInvestigationForm((current) => ({ ...current, findings: event.target.value }))} placeholder="Record verified findings and contributing factors." /></label>
            <label className="ncp-form-field wide"><span>Corrective actions</span><textarea rows={4} value={investigationForm.correctiveActions} onChange={(event) => setInvestigationForm((current) => ({ ...current, correctiveActions: event.target.value }))} placeholder="Document corrective and preventive actions." /></label>
            <label className="ncp-form-field"><span>Status</span><select value={investigationForm.status} onChange={(event) => setInvestigationForm((current) => ({ ...current, status: event.target.value }))}><option>In progress</option><option>Completed</option><option>Reopened</option></select></label>
          </div>
          <div className="incident-investigation-evidence">
            <h3>Investigation evidence</h3>
            <div className="incident-privacy-badge"><i className="ti ti-shield-lock" /><span><strong>Protect resident privacy.</strong> Do not add photos that compromise a resident's privacy or dignity.</span></div>
            <div className="incident-file-actions">
              <input ref={investigationFileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" hidden onChange={(event) => addPendingFiles(setInvestigationFiles, event)} />
              <button type="button" className="mini-btn" onClick={() => investigationFileInputRef.current?.click()}><i className="ti ti-paperclip" /> Attach Supporting Photos &amp; Docs</button>
            </div>
            {investigationFiles.length || selectedIncident?.attachmentFileIds?.length ? <div className="incident-attachment-thumbnails">
              {(selectedIncident?.attachmentFileIds || []).map((fileId, index) => {
                const attachment = (selectedIncident?.attachments || []).find((item) => item.fileId === fileId);
                return <IncidentAttachmentThumbnail key={fileId} url={attachment?.signedUrl} label={`Attachment ${index + 1}`} onPreview={setAttachmentPreview} />;
              })}
              {investigationFiles.map((file, index) => <IncidentAttachmentThumbnail key={`${file.name}-${file.lastModified}-${index}`} file={file} label={file.name} onPreview={setAttachmentPreview} />)}
            </div> : null}
            {investigationFiles.length || selectedIncident?.attachmentFileIds?.length ? <div className="incident-file-list">
              {(selectedIncident?.attachmentFileIds || []).map((fileId, index) => {
                const attachment = (selectedIncident?.attachments || []).find((item) => item.fileId === fileId);
                return <span key={fileId}><i className="ti ti-file-check" />{attachment?.signedUrl ? <a href={attachment.signedUrl} target="_blank" rel="noreferrer">Saved attachment {index + 1}</a> : <span>Saved attachment {index + 1}</span>}</span>;
              })}
              {investigationFiles.map((file, index) => <span key={`${file.name}-${file.lastModified}-${index}`}><i className="ti ti-file" /> {file.name}<button type="button" onClick={() => setInvestigationFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove ${file.name}`}><i className="ti ti-x" /></button></span>)}
            </div> : null}
          </div></section>
          <div className="assessment-drawer-actions"><button className="btn secondary" type="button" onClick={() => setInvestigationOpen(false)}>Cancel</button><button className="btn primary" type="submit" disabled={saving || incidentDetailLoading || incidentFilesUploading}>{incidentDetailLoading ? "Loading..." : saving || incidentFilesUploading ? "Saving..." : "Save investigation"}</button></div>
        </form>
      </Drawer> : null}

      {isIncidentModule ? <Drawer title={<div className="incident-details-title"><span><i className="ti ti-list-check" /></span><div><strong>Action Taken</strong><small>Document the response completed after investigation</small></div></div>} placement="right" width="min(720px, 96vw)" open={actionTakenOpen} onClose={() => setActionTakenOpen(false)} className="resident-action-drawer incident-action-taken-drawer">
        <form className="assessment-drawer-form" onSubmit={saveActionTaken}>
          <section className="assessment-drawer-section">
            <div className="incident-action-context"><i className="ti ti-file-search" /><div><span>Completed investigation</span><strong>{selectedIncident?.title}</strong></div></div>
            <div className="assessment-drawer-grid">
              <label className="ncp-form-field"><span>Action type</span><select required value={actionTakenForm.actionType} onChange={(event) => setActionTakenForm((current) => ({ ...current, actionType: event.target.value, otherActionType: event.target.value === "Other" ? current.otherActionType : "" }))}><option value="">Select</option>{["Corrective action", "Care plan update", "Staff coaching or training", "Medical follow-up", "Safety or environmental change", "Policy or procedure update", "External notification", "Other"].map((option) => <option key={option}>{option}</option>)}</select></label>
              <label className="ncp-form-field"><span>Completed at</span><input required type="datetime-local" value={actionTakenForm.completedAt} onChange={(event) => setActionTakenForm((current) => ({ ...current, completedAt: event.target.value }))} /></label>
              {actionTakenForm.actionType === "Other" ? <label className="ncp-form-field wide"><span>Specify other action type</span><input required value={actionTakenForm.otherActionType || ""} onChange={(event) => setActionTakenForm((current) => ({ ...current, otherActionType: event.target.value }))} placeholder="Enter the action type" /></label> : null}
              <label className="ncp-form-field wide"><span>Action taken by</span><Select mode="multiple" labelInValue showSearch filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())} value={actionTakenForm.staffMembers || []} options={incidentStaffOptions} loading={incidentStaffSearchLoading} onSearch={(query) => searchIncidentPeople("staff", query)} onChange={(value) => setActionTakenForm((current) => ({ ...current, staffMembers: value }))} placeholder="Select the staff who completed the action" /></label>
              <label className="ncp-form-field wide"><span>What actions were taken</span><textarea required rows={4} value={actionTakenForm.summary} onChange={(event) => setActionTakenForm((current) => ({ ...current, summary: event.target.value }))} placeholder="Describe each action completed in response to the investigation." /></label>
              <label className="ncp-form-field wide"><span>Prevention of future occurrences</span><textarea required rows={4} value={actionTakenForm.preventiveActions} onChange={(event) => setActionTakenForm((current) => ({ ...current, preventiveActions: event.target.value }))} placeholder="Describe the safeguards, training, care-plan changes, or process improvements put in place." /></label>
              <label className="ncp-form-field wide"><span>Outcome</span><textarea rows={3} value={actionTakenForm.outcome} onChange={(event) => setActionTakenForm((current) => ({ ...current, outcome: event.target.value }))} placeholder="Record the result and current condition." /></label>
              <label className="ncp-form-field wide"><span>Follow-up notes</span><textarea rows={3} value={actionTakenForm.followUpNotes} onChange={(event) => setActionTakenForm((current) => ({ ...current, followUpNotes: event.target.value }))} placeholder="Add monitoring, review, or further action needed." /></label>
              <label className="ncp-form-field"><span>Status</span><select value={actionTakenForm.status} onChange={(event) => setActionTakenForm((current) => ({ ...current, status: event.target.value }))}><option>In progress</option><option>Completed</option><option>Follow-up required</option></select></label>
            </div>
          </section>
          <div className="assessment-drawer-actions"><button className="btn secondary" type="button" onClick={() => setActionTakenOpen(false)}>Cancel</button><button className="btn primary" type="submit" disabled={saving || incidentDetailLoading || !(actionTakenForm.staffMembers || []).length}>{incidentDetailLoading ? "Loading..." : saving ? "Saving..." : "Save action taken"}</button></div>
        </form>
      </Drawer> : null}

      {isIncidentModule && selectedIncident ? createPortal(<article className="incident-print-record">
        <header><div><span>OCarely Incident Record</span><h1>{selectedIncident.title}</h1><p>{workspaceName}</p></div><strong>{statusLabel(selectedIncident.status)}</strong></header>
        <section className="incident-print-summary"><div><span>Incident type</span><strong>{selectedIncident.category || "Not recorded"}</strong></div><div><span>Severity</span><strong>{selectedIncident.severity || "Not recorded"}</strong></div><div><span>Occurred</span><strong>{formatDateTime(selectedIncident.occurredAt)}</strong></div><div><span>Location</span><strong>{selectedIncident.location || "Not recorded"}</strong></div>{incidentIsClosed(selectedIncident) ? <><div><span>Closed</span><strong>{selectedIncident.closedAt ? formatDateTime(selectedIncident.closedAt) : "Yes"}</strong></div><div><span>Closing notes</span><strong>{selectedIncident.closingNotes || "None recorded"}</strong></div></> : null}</section>
        <section><h2>Incident Details</h2><dl><div><dt>Residents involved</dt><dd>{(selectedIncident.residentIds || []).map((id) => incidentResidentOptions.find((option) => option.value === id)?.label || id).join(", ") || "None recorded"}</dd></div><div><dt>Staff involved</dt><dd>{(selectedIncident.staffIds || []).map((id) => incidentStaffOptions.find((option) => option.value === id)?.label || id).join(", ") || "None recorded"}</dd></div><div><dt>What happened</dt><dd>{selectedIncident.description || "Not recorded"}</dd></div><div><dt>Immediate actions</dt><dd>{selectedIncident.immediateActions || "Not recorded"}</dd></div><div><dt>People and agencies notified</dt><dd>{selectedIncident.notificationsMade || "Not recorded"}</dd></div><div><dt>Witnesses</dt><dd>{selectedIncident.witnesses || "None recorded"}</dd></div><div><dt>Follow-up required</dt><dd>{selectedIncident.followUpRequired || "Not recorded"}</dd></div><div><dt>Follow-up plan</dt><dd>{selectedIncident.followUpPlan || "Not recorded"}</dd></div><div><dt>Supporting attachments</dt><dd>{selectedIncident.attachments?.length || 0}</dd></div></dl></section>
        <section><h2>Investigation Report</h2><div className="incident-print-meta"><span>Status: <strong>{selectedIncident.investigationStatus || "Not started"}</strong></span><span>Investigated: <strong>{selectedIncident.investigatedAt ? formatDateTime(selectedIncident.investigatedAt) : "Not recorded"}</strong></span></div><dl><div><dt>Summary</dt><dd>{selectedIncident.investigationSummary || "Not recorded"}</dd></div><div><dt>Findings</dt><dd>{selectedIncident.investigationFindings || "Not recorded"}</dd></div><div><dt>Corrective actions</dt><dd>{selectedIncident.correctiveActions || "Not recorded"}</dd></div></dl></section>
        <section><h2>Actions Taken</h2><div className="incident-print-meta"><span>Status: <strong>{selectedIncident.actionTakenStatus || "Not started"}</strong></span><span>Completed: <strong>{selectedIncident.actionTakenAt ? formatDateTime(selectedIncident.actionTakenAt) : "Not recorded"}</strong></span></div><dl><div><dt>Action type</dt><dd>{selectedIncident.actionTakenType === "Other" ? selectedIncident.actionTakenOtherType || "Other" : selectedIncident.actionTakenType || "Not recorded"}</dd></div><div><dt>Action taken by</dt><dd>{(selectedIncident.actionTakenStaffIds || []).map((id) => incidentStaffOptions.find((option) => option.value === id)?.label || id).join(", ") || "Not recorded"}</dd></div><div><dt>What actions were taken</dt><dd>{selectedIncident.actionTakenSummary || "Not recorded"}</dd></div><div><dt>Prevention of future occurrences</dt><dd>{selectedIncident.actionTakenPreventiveActions || "Not recorded"}</dd></div><div><dt>Outcome</dt><dd>{selectedIncident.actionTakenOutcome || "Not recorded"}</dd></div><div><dt>Follow-up notes</dt><dd>{selectedIncident.actionTakenFollowUpNotes || "Not recorded"}</dd></div></dl></section>
        <footer>Printed {formatDateTime(new Date())}</footer>
      </article>, document.body) : null}

      <Modal className="incident-attachment-preview-modal" title={<div className="incident-attachment-preview-title"><span><i className="ti ti-file-search" /></span><div><strong>Attachment Preview</strong><small>{attachmentPreview?.label || "Supporting photo or document"}</small></div></div>} open={Boolean(attachmentPreview)} onCancel={() => setAttachmentPreview(null)} footer={null} centered width="min(920px, 94vw)" destroyOnClose>
        {attachmentPreview?.isImage ? <img src={attachmentPreview.url} alt={attachmentPreview.label || "Attachment"} /> : attachmentPreview?.url ? <iframe src={attachmentPreview.url} title={attachmentPreview.label || "Attachment preview"} /> : <div className="incident-attachment-preview-empty"><i className="ti ti-file-off" /><p>Preview is not available for this attachment.</p></div>}
      </Modal>
    </CareShell>
  );
}

export default OCareFeaturePage;
